import React, { useEffect, useRef, useState } from "react";
import LeagueShell from "./LeagueShell";
import LeagueComposer from "./LeagueComposer";
import LeaguePostModal from "./LeaguePostModal";
import LeagueColumns from "./LeagueColumns";
import FeedPage from "../../FeedPage";
import { ChevronRightIcon } from "../landing/icons";
import { Link } from "react-router-dom";
import { scrollToTop } from "../landing/wizard/wizardScroll";
import { competitionCopy } from "../landing/leagueFormat";
import LeagueRankRail from "./LeagueRankRail";

// A league's Home — what a member lands on, straight after joining.
//
// The feed IS the league, so it takes the main column and the standings sit in
// the rail beside it. The feed itself is the existing FeedPage (posts, comments,
// likes, reposts) scoped to this game — rebuilding that here would fork it.
const LeagueHome = ({ game }) => {
  // The client's redesign covers the PUBLIC league home; private keeps its own.
  const isPublic = game?.game_type !== "private";
  // A CASH GAME's rail starts at the identity header, not at the feed, so that
  // the prize card can sit level with the CASH GAME eyebrow and the Leaderboard
  // card can sit level with the Game feed heading — client: "so the two columns
  // start on the same top edge". That means the shell renders it, not the body
  // grid below. Every other competition type is untouched.
  const railInShell = !!game?.is_cash_prize_league;
  const [composerOpen, setComposerOpen] = useState(false);
  // FeedPage loads its own posts once, so a fresh post needs it remounted. Bumping
  // this key is cheaper than threading a refetch handle out of a 4,800-line screen.
  const [feedKey, setFeedKey] = useState(0);

  // Client Rev 3, 5: "On desktop, I joined a league that already had enough feed
  // posts to make League Home scrollable. After joining, I was dropped near the
  // bottom of the League Home page instead of at the top. When entering or
  // joining a league, League Home should always open at the top of the page for
  // both mobile and desktop versions."
  //
  // Joining happens from the BOTTOM of the landing page — the Join League CTA is
  // the last thing on it — and joining does not change the address: LeagueEntry
  // re-resolves and swaps the landing for this screen in place. React preserves
  // the scroll offset across that swap, so the new member arrived at whatever
  // offset the CTA had been at. Entering from a link had the same effect whenever
  // the previous screen was scrolled.
  //
  // `scrollToTop` (not window.scrollTo) because which element scrolls differs by
  // route: it walks up from this node to the real scroller and resets the window
  // as well. Keyed on the league id so switching leagues also opens at the top.
  const topRef = useRef(null);
  useEffect(() => {
    scrollToTop(topRef.current);
  }, [game?.id]);

  return (
    <LeagueShell
      game={game}
      active="home"
      rail={railInShell ? <LeagueRankRail game={game} /> : null}
    >
      <div ref={topRef} aria-hidden="true" />
      {/* Mobile puts the standings up front as one tappable card — the desktop
          rail has no room to live on a phone, and burying it under the feed meant
          nobody saw it. Desktop keeps the rail (see below). */}
      <Link
        to={`/${game?.vanity_url}/leaderboard`}
        className="lg:hidden mt-4 flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4"
      >
        {/* Client Rev 2: "The Leaderboard icon on the public and private league
            home pages needs to be changed. Use this; make sure the background is
            transparent."
            DONE — the client's asset is in. public/podium.png is now the square
            three-step teal podium with gold 1/2/3 and the QUSHi coin on top,
            841x841 RGBA, cut out so 50% of it is fully transparent.

            What it replaced was a WHITE, EMPTY round pedestal. Its alpha channel
            was fine, which is why "make the background transparent" did not
            describe the real fault — white art on a white card is invisible
            whatever the alpha says. It was also 1536x1024 (3:2) being squeezed
            into a 44px square, so it rendered 44x29 and read as a smudge. The
            interim `w-16 aspect-[3/2]` only made an invisible image bigger.

            Square art means the box goes back to the client's own w-11 h-11. */}
        <img
          src="/podium.png"
          alt=""
          className="w-11 h-11 object-contain shrink-0"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <span className="flex-1 min-w-0">
          <span className="block font-montserrat font-bold text-base text-[#0F172A]">
            Leaderboard
          </span>
          <span className="block text-xs text-secondary">See where you rank</span>
        </span>
        <ChevronRightIcon className="w-5 h-5 shrink-0 text-[#0F4146]" />
      </Link>

      <div className="mt-6">
        {/* Site-wide type rule: the screen title (the league's name, up in the
            shell) is the only thing that takes the dark-teal token, and section
            headers are Montserrat near-black.
            Client Rev 4: "'League feed' font color should be: #1A1A1A (both
            desktop and mobile need the change)." The public redesign had already
            moved to #1A1A1A; private was still on the dark teal it was built
            with, which is why the two screens disagreed. One colour now, so they
            cannot drift again. */}
        {/* Client 0014889: "League feed -> Game feed" on a Cash Game. */}
        <h2 className="font-montserrat font-bold text-lg md:text-2xl text-[#1A1A1A]">
          {competitionCopy(game).feedTitle}
        </h2>
        {/* Client Rev 4: "Keep the subtitle in the existing secondary gray" —
            text-secondary, untouched. */}
        <p className="mt-0.5 text-sm text-secondary">
          {isPublic ? (
            "Posts, charts, and competition talk"
          ) : (
            <>
              <span className="hidden md:inline">
                Posts, charts, and competition talk
              </span>
              <span className="md:hidden">
                Your league. Your edge. Keep it in the circle.
              </span>
            </>
          )}
        </p>
        {/* Client Rev 4: "Add a 1px faint divider across the content width below
            the subtitle. About 12px from subtitle to divider." mt-3 is 12px, and
            border-divider is the same 1px hairline the identity header closes
            with, so the two rules on this screen match. It sits outside the
            column grid, so it spans the full content width including the rail —
            "across the content width". */}
        <div className="mt-3 border-t border-divider" />
      </div>

      {/* Rail is fixed-width on desktop and drops below the feed on mobile.
          mt-2.5 is 10px — client Rev 4: "About 10px from divider to the first
          post." (It was mt-4/16px, measured from the subtitle with no divider.)
          The grid itself now lives in LeagueColumns, which Portfolio and Watchlist
          share — client Rev 2: "Can Home's existing main column plus sticky right
          rail layout be reused as a shared shell for Portfolio and Watchlist." */}
      <LeagueColumns game={game} className="mt-2.5" withRail={!railInShell}>
          {/* Desktop only. On mobile the bottom bar's "+" is the compose action —
              it routes to this league's create-post screen. */}
          <div className="hidden md:block">
            <LeagueComposer game={game} onOpen={() => setComposerOpen(true)} />
          </div>
          {/* `embedded` keeps FeedPage to just the feed — this screen already owns
              the header, the container, the league navigation and the composer. */}
          <FeedPage
            key={feedKey}
            gameId={game?.id}
            slug={game?.vanity_url}
            game={game}
            embedded
            // The feed's empty state offers "Create first post" / "Create a post"
            // (client Rev 2, item 10); the modal lives here, so the opener goes down.
            onCompose={() => setComposerOpen(true)}
          />
      </LeagueColumns>

      <LeaguePostModal
        open={composerOpen}
        game={game}
        onClose={() => setComposerOpen(false)}
        onPosted={() => setFeedKey((k) => k + 1)}
      />
    </LeagueShell>
  );
};

export default LeagueHome;
